import React, { useEffect, useMemo } from "react";
import {
  buildCartBillSummary,
  formatSavingsBannerText,
} from "../../utils/cart-bill-summary";

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);

/**
 * Bill summary modal — Shopflo-style breakdown + full-width checkout.
 */
export default function CartBillSummarySheet({
  open,
  onClose,
  cart,
  formatMoney,
  rewardSection = null,
  discountCodeLabels = {},
  embeddedInEditor = false,
  showSavingsBanner = true,
  savingsBannerBackground = "#e6f4ea",
  savingsBannerColor = "#2e7d32",
  savingsBannerText = "🎉 You're saving {amount} 🥳 !",
  billSummaryTitle = "Bill Summary",
  billSummaryTitleStyle = {},
  billSummaryLineStyle = {},
  billSummaryMetaLineStyle = null,
  detailLayout = false,
  sellingSubtotalLabel = "Subtotal",
  totalMrpLabel = "Total MRP",
  deliveryFeeLabel = "Delivery fee",
  deliveryFeeText = "To be calculated",
  mrpDiscountLabel = "Discount on MRP",
  couponDiscountLabel = "Coupon discount",
  grandTotalLabel = "Grand total",
  /** @deprecated Prefer totalMrpLabel — kept for older callers */
  subtotalLabel = "Subtotal",
  showDiscounts = true,
  discountAmountColor = "#2e7d32",
  removingDiscount = false,
  onRemoveDiscount,
  taxNoteText = "",
  taxNoteStyle = {},
  showCheckout = true,
  checkoutLabel = "Secure Checkout",
  checkoutButtonStyle = null,
  checkoutLoading = false,
  checkoutDisabled = false,
  showCheckoutLock = true,
  showCheckoutSparkle = false,
  checkoutLeftIcon = null,
  checkoutRightIcon = null,
  onCheckout,
}) {
  const summary = useMemo(
    () =>
      buildCartBillSummary(cart, {
        rewardSection,
        formatMoney,
        discountCodeLabels,
        totalMrpLabel,
        billSubtotalLabel: totalMrpLabel,
        subtotalLabel: totalMrpLabel,
        deliveryFeeLabel,
        deliveryFeeText,
        mrpDiscountLabel,
        couponDiscountLabel,
        grandTotalLabel,
        sellingSubtotalLabel,
        detailLayout,
        showDiscounts,
      }),
    [
      cart,
      rewardSection,
      formatMoney,
      discountCodeLabels,
      totalMrpLabel,
      sellingSubtotalLabel,
      detailLayout,
      deliveryFeeLabel,
      deliveryFeeText,
      mrpDiscountLabel,
      couponDiscountLabel,
      grandTotalLabel,
      showDiscounts,
      open,
    ]
  );

  useEffect(() => {
    if (!open || embeddedInEditor) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [embeddedInEditor, onClose, open]);

  if (!open) return null;

  const amountText = formatMoney(summary.totalSavingsCents);
  const bannerCopy = formatSavingsBannerText(savingsBannerText, amountText);
  const showBanner = showSavingsBanner && summary.totalSavingsCents > 0;

  const handleCheckoutClick = () => {
    if (embeddedInEditor || checkoutDisabled || checkoutLoading) return;
    onCheckout?.();
  };

  return (
    <div className="weu-custom-cart-drawer__sheet-root" role="presentation">
      <button
        type="button"
        className="weu-custom-cart-drawer__sheet-backdrop"
        onClick={embeddedInEditor ? undefined : onClose}
        aria-label="Close bill summary"
        tabIndex={embeddedInEditor ? -1 : 0}
      />
      <div
        className="weu-custom-cart-drawer__sheet-panel weu-custom-cart-drawer__sheet-panel--bill-summary"
        role="dialog"
        aria-modal="true"
        aria-label={billSummaryTitle}
      >
        <div className="weu-custom-cart-drawer__sheet-header">
          <h3
            className="weu-custom-cart-drawer__sheet-title"
            style={billSummaryTitleStyle}
          >
            {billSummaryTitle}
          </h3>
          <button
            type="button"
            className="weu-custom-cart-drawer__sheet-close"
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="weu-custom-cart-drawer__sheet-body weu-custom-cart-drawer__sheet-body--flush">
          {showBanner ? (
            <div
              className="weu-custom-cart-drawer__savings-banner weu-custom-cart-drawer__savings-banner--in-sheet"
              style={{
                backgroundColor: savingsBannerBackground,
                color: savingsBannerColor,
              }}
            >
              {bannerCopy}
            </div>
          ) : null}

          <div
            className={[
              "weu-custom-cart-drawer__bill-lines",
              detailLayout
                ? "weu-custom-cart-drawer__bill-lines--detail"
                : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {summary.lines.map((line) => {
              const isDiscount = line.kind === "discount" || line.accent;
              const isGrand = line.kind === "grand";
              const isMuted = line.kind === "muted";
              const isMeta = line.kind === "meta";
              const isSubtotal = line.kind === "subtotal";
              return (
                <div
                  key={line.key}
                  className={[
                    "weu-custom-cart-drawer__bill-line",
                    isDiscount
                      ? "weu-custom-cart-drawer__bill-line--discount"
                      : "",
                    isGrand ? "weu-custom-cart-drawer__bill-line--grand" : "",
                    isMuted ? "weu-custom-cart-drawer__bill-line--muted" : "",
                    isMeta ? "weu-custom-cart-drawer__bill-line--meta" : "",
                    isSubtotal
                      ? "weu-custom-cart-drawer__bill-line--subtotal"
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  style={{
                    ...(isMeta && billSummaryMetaLineStyle
                      ? billSummaryMetaLineStyle
                      : billSummaryLineStyle),
                    ...(isDiscount
                      ? {
                          color: discountAmountColor,
                          // Keep discount rows a step smaller than MRP / grand total.
                          fontSize: "12px",
                        }
                      : null),
                  }}
                >
                  <span className="weu-custom-cart-drawer__bill-line-label">
                    {line.label}
                    {line.removable && !embeddedInEditor && onRemoveDiscount ? (
                      <button
                        type="button"
                        className="weu-custom-cart-drawer__discount-chip-remove"
                        onClick={() => onRemoveDiscount(line.code)}
                        disabled={removingDiscount}
                        aria-label={`Remove ${line.label}`}
                      >
                        <CloseIcon />
                      </button>
                    ) : null}
                  </span>
                  <span className="weu-custom-cart-drawer__bill-line-value">
                    {line.valueText != null
                      ? line.valueText
                      : line.valueCents < 0
                        ? `- ${formatMoney(Math.abs(line.valueCents))}`
                        : formatMoney(line.valueCents)}
                  </span>
                </div>
              );
            })}
          </div>

          {taxNoteText ? (
            <p
              className="weu-custom-cart-drawer__bill-tax-note"
              style={taxNoteStyle}
            >
              {taxNoteText}
            </p>
          ) : null}

          {showCheckout ? (
            <button
              type="button"
              className={`weu-custom-cart-drawer__checkout weu-custom-cart-drawer__checkout--in-sheet${
                checkoutLoading
                  ? " weu-custom-cart-drawer__checkout--loading"
                  : ""
              }`}
              style={checkoutButtonStyle || undefined}
              disabled={
                embeddedInEditor ? false : checkoutDisabled || checkoutLoading
              }
              aria-busy={checkoutLoading}
              onClick={handleCheckoutClick}
            >
              {checkoutLoading ? (
                <>
                  <span
                    className="weu-custom-cart-drawer__spinner weu-custom-cart-drawer__spinner--sm"
                    aria-hidden
                  />
                  <span>Processing…</span>
                </>
              ) : (
                <span className="weu-custom-cart-drawer__checkout-inner">
                  {showCheckoutLock && checkoutLeftIcon ? (
                    <span className="weu-custom-cart-drawer__checkout-icon">
                      {checkoutLeftIcon}
                    </span>
                  ) : null}
                  <span className="weu-custom-cart-drawer__checkout-label">
                    {checkoutLabel}
                  </span>
                  {showCheckoutSparkle && checkoutRightIcon ? (
                    <span className="weu-custom-cart-drawer__checkout-icon weu-custom-cart-drawer__checkout-icon--sparkle">
                      {checkoutRightIcon}
                    </span>
                  ) : null}
                </span>
              )}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function CartSavingsBanner({
  visible,
  amountCents,
  formatMoney,
  backgroundColor = "#e6f4ea",
  color = "#2e7d32",
  text = "🎉 You're saving {amount} 🥳 !",
}) {
  if (!visible || !(amountCents > 0)) return null;
  const copy = formatSavingsBannerText(text, formatMoney(amountCents));
  return (
    <div
      className="weu-custom-cart-drawer__savings-banner"
      style={{ backgroundColor, color }}
    >
      {copy}
    </div>
  );
}
